/**
 * SI-16: supplier-side Supplier Performance — one row per customer.
 *
 * The customer page ranks "my suppliers"; the supplier page shows "how each of
 * my customers rates me". The rows come from `/suppliers/profiles/by-customer/`
 * (the same live serializer the customer page uses, scoped per customer) and
 * are filtered client-side here — a supplier trades with a handful of
 * customers, so the server returns them all and only the date window is a
 * server-side parameter. Kept out of `useSupplierPerformance` so that hook
 * gains a branch, not a second body.
 */
import { SupplierProfile, SupplierSelfPerformanceRow } from '../../lib/api';
import { SupplierPerformanceFilterValues } from '../../components/suppliers/SupplierPerformanceFilters';
import { getGradeInfo, isNARating } from './utils';

/** Type guard: a row from the by-customer endpoint (carries its customer). */
export function selfRowOf(row: SupplierProfile | SupplierSelfPerformanceRow): SupplierSelfPerformanceRow | null {
  return 'customer_id' in row && typeof row.customer_id === 'number'
    ? (row as SupplierSelfPerformanceRow)
    : null;
}

/** Stable React key — rows are (supplier site × customer) pairs. */
export function selfRowKey(row: SupplierProfile | SupplierSelfPerformanceRow): string | number {
  const self = selfRowOf(row);
  return self ? `${self.supplier_site_id}-${self.customer_id}` : row.id;
}

/** Give each fetched row a unique client key the table can hand back on drill-downs. */
export function keySelfRows(rows: SupplierSelfPerformanceRow[]): SupplierSelfPerformanceRow[] {
  return rows.map((row, i) => ({ ...row, self_key: i + 1 }));
}

/** Grade label for a row using THAT customer's bands (F6). */
export function selfRowGrade(row: SupplierSelfPerformanceRow): string {
  return getGradeInfo(row.combined_score, row.rating_thresholds).label;
}

/**
 * Server-side params for the by-customer fetch: only the date window and
 * currency travel to the server. Everything else is a client-side filter.
 */
export function buildSelfParams(
  filters: SupplierPerformanceFilterValues,
  currency: string,
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = { currency };
  if (filters.dateFrom) params['date_from'] = filters.dateFrom;
  if (filters.dateTo) params['date_to'] = filters.dateTo;
  return params;
}

/**
 * Client-side filters for the supplier page. `division` is the customer group
 * and `site` the customer site — the same meaning those two fields carry on
 * the customer page's filter panel.
 */
export function filterSelfRows(
  rows: SupplierSelfPerformanceRow[],
  filters: SupplierPerformanceFilterValues,
  search: string,
): SupplierSelfPerformanceRow[] {
  const needle = search.trim().toLowerCase();
  return rows.filter((row) => {
    if (filters.supplierSite !== 'all' && String(row.supplier_site_id) !== filters.supplierSite) return false;
    if (filters.division !== 'all' && (row.customer_group ?? '') !== filters.division) return false;
    if (filters.site !== 'all' && String(row.customer_id) !== filters.site) return false;
    if (needle && !row.customer_name.toLowerCase().includes(needle)) return false;
    const na = isNARating(row);
    if (filters.excludeNARatings && na) return false;
    if (filters.rating === 'all') return true;
    if (filters.rating === 'N/A') return na;
    if (na) return false;
    const grade = getGradeInfo(row.combined_score, row.rating_thresholds);
    return grade.grade === filters.rating || grade.label === filters.rating;
  });
}

/** Options for the supplier panel's pickers, derived from the rows themselves. */
export function selfFilterOptions(rows: SupplierSelfPerformanceRow[]) {
  const groups = Array.from(new Set(rows.map((r) => r.customer_group).filter((g): g is string => !!g))).sort();
  // De-duplicated: at GROUP view two supplier sites can trade with the same customer.
  const sites = Array.from(
    new Map(rows.map((r) => [r.customer_id, { id: r.customer_id, name: r.customer_name, group: r.customer_group }])).values(),
  ).sort((a, b) => a.name.localeCompare(b.name));
  const grades = Array.from(
    new Set(rows.flatMap((r) => r.rating_thresholds.map((t) => t.grade))),
  );
  // Q8: at GROUP view rows span sibling supplier sites — offer a Supplier Site
  // picker (and the table a column) only when there is more than one.
  const supplierSites = Array.from(
    new Map(rows.map((r) => [r.supplier_site_id, { id: r.supplier_site_id, name: r.supplier_site_name }])).values(),
  ).sort((a, b) => a.name.localeCompare(b.name));
  return { groups, sites, grades, supplierSites };
}

/**
 * Drill-down params for a supplier-page row: the supplier is the ROW's site
 * (at GROUP view rows span sibling sites), the customer is the row's customer.
 * Each list endpoint names the customer differently, hence one key per type.
 */
export function buildSelfDrilldownParams(
  kind: 'po' | 'pol' | 'grn' | 'ncr',
  row: SupplierSelfPerformanceRow,
  filters: SupplierPerformanceFilterValues,
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = { supplier: row.supplier_site_id };
  const customerKey = kind === 'pol' ? 'po__customer' : kind === 'grn' ? 'po_line__po__customer' : 'customer';
  params[customerKey] = row.customer_id;
  // Q7: the NCR list must return the same set the cell counts.
  if (kind === 'ncr') params['judged'] = true;
  if (filters.dateFrom) params['date_from'] = filters.dateFrom;
  if (filters.dateTo) params['date_to'] = filters.dateTo;
  return params;
}
